import { useState } from "react";
import { Plus, Trash2, Plane, BedDouble } from "lucide-react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import styles from "./SupportPackageModal.module.css";

/**
 * Add / Edit Support Level (Support spec v1.0 §6.2).
 *
 * §6.2 fixes the five blocks: Identity, Product & Pricing, Status, Services, Actions. The two
 * that carry the real meaning:
 *
 * PRODUCT & PRICING (§3.1) is a separate block per product, not one price with a product
 * checkbox. "Price and pricing unit are configured independently per product", and a level may
 * be sold for one product or both at different prices.
 *
 * SERVICES (§5) assigns from the central catalogue, and the fee fields appear only for Paid -
 * §12 says Included and Not Included "must not require fee fields", so they are not rendered at
 * all rather than rendered and ignored.
 */

const PRODUCTS = [
  { key: "FLIGHT_ONLY", label: "Flight Only", Icon: Plane },
  { key: "FLIGHT_HOTEL", label: "Flight + Hotel", Icon: BedDouble },
];

const PRICING_UNITS = [
  { value: "PER_PERSON", label: "Per person" },
  { value: "PER_BOOKING", label: "Per booking" },
];

const FEE_UNITS = [
  { value: "PER_REQUEST", label: "Per request" },
  { value: "PER_PERSON", label: "Per person" },
  { value: "PER_BOOKING", label: "Per booking" },
];

const STATUSES = [
  { value: "INCLUDED", label: "Included" },
  { value: "PAID", label: "Paid" },
  { value: "NOT_INCLUDED", label: "Not included" },
];

const ASSIGNMENT_PRODUCTS = [
  { value: "BOTH", label: "Both" },
  { value: "FLIGHT_ONLY", label: "Flight Only" },
  { value: "FLIGHT_HOTEL", label: "Flight + Hotel" },
];

const emptyProduct = (productType) => ({
  productType,
  enabled: false,
  price: "",
  pricingUnit: "PER_PERSON",
  isDefault: false,
});

const toForm = (level) => {
  if (!level) {
    return {
      name: "",
      code: "",
      customerTitle: "",
      badge: "",
      shortDescription: "",
      internalDescription: "",
      sortOrder: 0,
      active: true,
      products: PRODUCTS.map((p) => emptyProduct(p.key)),
      services: [],
    };
  }
  return {
    name: level.name ?? "",
    code: level.code ?? "",
    customerTitle: level.customerTitle ?? "",
    badge: level.badge ?? "",
    shortDescription: level.shortDescription ?? "",
    internalDescription: level.internalDescription ?? "",
    sortOrder: level.sortOrder ?? 0,
    active: level.active ?? true,
    products: PRODUCTS.map((p) => {
      const existing = level.products?.[p.key];
      return existing
        ? {
            productType: p.key,
            enabled: !!existing.enabled,
            price: existing.price == null ? "" : String(existing.price),
            pricingUnit: existing.pricingUnit || "PER_PERSON",
            isDefault: !!existing.isDefault,
          }
        : emptyProduct(p.key);
    }),
    services: (level.services ?? []).map((s) => ({
      _key: `${s.id}`,
      supportServiceId: s.supportServiceId,
      productType: s.productType,
      status: s.status,
      serviceFee: s.serviceFee == null ? "" : String(s.serviceFee),
      feeUnit: s.feeUnit || "PER_REQUEST",
      sortOrder: s.sortOrder ?? 0,
    })),
  };
};

const SupportLevelModal = ({ level, services: catalogue, saving, onClose, onSave }) => {
  const [form, setForm] = useState(() => toForm(level));
  const [errors, setErrors] = useState({});
  const isEdit = !!level;
  // §7: the code freezes once the level has been sold.
  const codeLocked = isEdit && !!level.used;

  const set = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const setProduct = (productType, patch) =>
    setForm((f) => ({
      ...f,
      products: f.products.map((p) => (p.productType === productType ? { ...p, ...patch } : p)),
    }));

  const setService = (key, patch) =>
    setForm((f) => ({
      ...f,
      services: f.services.map((s) => (s._key === key ? { ...s, ...patch } : s)),
    }));

  const addService = () => {
    const firstUnused = catalogue.find((c) => !form.services.some((s) => s.supportServiceId === c.id));
    if (!firstUnused) return;
    setForm((f) => ({
      ...f,
      services: [
        ...f.services,
        {
          _key: `new-${Date.now()}-${Math.random()}`,
          supportServiceId: firstUnused.id,
          productType: "BOTH",
          status: "INCLUDED",
          serviceFee: "",
          feeUnit: "PER_REQUEST",
          sortOrder: f.services.length,
        },
      ],
    }));
  };

  /**
   * §5.2 duplicate detection, mirrored client-side.
   *
   * BOTH covers each single product, so (serviceX, BOTH) and (serviceX, FLIGHT_ONLY) are a
   * duplicate for Flight Only even though the rows differ. The server refuses this too; doing
   * it here means the operator sees which row is wrong instead of a toast about the whole form.
   */
  const duplicateKeys = () => {
    const claimed = new Map();
    const bad = new Set();
    for (const s of form.services) {
      const concrete = s.productType === "BOTH" ? ["FLIGHT_ONLY", "FLIGHT_HOTEL"] : [s.productType];
      const seen = claimed.get(s.supportServiceId) || new Set();
      for (const p of concrete) {
        if (seen.has(p)) bad.add(s._key);
        seen.add(p);
      }
      claimed.set(s.supportServiceId, seen);
    }
    return bad;
  };

  const dupes = duplicateKeys();

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Level name is required";
    if (!form.code.trim()) next.code = "Code is required";
    else if (!/^[A-Za-z0-9_-]+$/.test(form.code.trim())) {
      next.code = "Use only letters, numbers, underscore and hyphen";
    }
    if (!form.customerTitle.trim()) next.customerTitle = "Customer-facing title is required";

    // §12: an active level needs at least one available product.
    if (form.active && !form.products.some((p) => p.enabled)) {
      next.products = "An active support level needs at least one available product";
    }
    // §12: price is required and must be >= 0 for an available product. Zero is legal.
    for (const p of form.products.filter((x) => x.enabled)) {
      if (p.price === "" || Number.isNaN(Number(p.price)) || Number(p.price) < 0) {
        next.products = "Each available product needs a price of 0 or more";
      }
    }
    // §12: Paid requires a fee above zero and a unit.
    for (const s of form.services.filter((x) => x.status === "PAID")) {
      if (s.serviceFee === "" || !(Number(s.serviceFee) > 0)) {
        next.services = "A paid service needs a fee above 0";
      }
    }
    if (dupes.size) next.services = "The same service is assigned twice for a product";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      name: form.name.trim(),
      customerTitle: form.customerTitle.trim(),
      badge: form.badge.trim() || null,
      shortDescription: form.shortDescription.trim() || null,
      internalDescription: form.internalDescription.trim() || null,
      sortOrder: Number(form.sortOrder) || 0,
      active: form.active,
      products: form.products.map((p) => ({
        productType: p.productType,
        enabled: p.enabled,
        // The server nulls these for an unavailable product; sending them anyway would just be
        // noise, so an unavailable block submits nothing but its flag.
        ...(p.enabled ? { price: Number(p.price), pricingUnit: p.pricingUnit, isDefault: p.isDefault } : {}),
      })),
      services: form.services.map((s, i) => ({
        supportServiceId: Number(s.supportServiceId),
        productType: s.productType,
        status: s.status,
        sortOrder: i,
        ...(s.status === "PAID" ? { serviceFee: Number(s.serviceFee), feeUnit: s.feeUnit } : {}),
      })),
    };
    if (!codeLocked) payload.code = form.code.trim().toUpperCase();
    onSave(payload);
  };

  const nameOf = (id) => catalogue.find((c) => c.id === Number(id))?.name ?? `Service ${id}`;

  return (
    <BaseModal
      open
      onClose={onClose}
      title={isEdit ? "Edit Support Level" : "Add Support Level"}
      width="860px"
      footer={
        <div className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="support-level-form" className={styles.primary} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Level"}
          </button>
        </div>
      }
    >
      <form id="support-level-form" className={styles.form} onSubmit={submit}>
        {/* ── 1. Identity (§6.2.1) ── */}
        <div className={styles.sectionTitle}>Identity</div>
        <div className={styles.grid2}>
          <div className={styles.field}>
            <label>Level Name *</label>
            <input value={form.name} onChange={set("name")} placeholder="e.g. Premium"
              className={errors.name ? styles.invalid : undefined} />
            {errors.name && <span className={styles.err}>{errors.name}</span>}
          </div>
          <div className={styles.field}>
            <label>Code *</label>
            <input value={form.code} onChange={set("code")} placeholder="e.g. PREMIUM" disabled={codeLocked}
              className={errors.code ? styles.invalid : undefined} />
            {errors.code && <span className={styles.err}>{errors.code}</span>}
            {codeLocked && <span className={styles.hint}>Used on a booking, so the code is locked.</span>}
          </div>
          <div className={styles.field}>
            <label>Customer-facing Title *</label>
            <input value={form.customerTitle} onChange={set("customerTitle")} placeholder="e.g. Premium Support"
              className={errors.customerTitle ? styles.invalid : undefined} />
            {errors.customerTitle && <span className={styles.err}>{errors.customerTitle}</span>}
          </div>
          <div className={styles.field}>
            <label>Customer Badge / Tag</label>
            <input value={form.badge} onChange={set("badge")} placeholder="e.g. Most Popular" />
            <span className={styles.hint}>The pill shown on the checkout card.</span>
          </div>
        </div>
        <div className={styles.field}>
          <label>Customer Short Description</label>
          <textarea rows={2} value={form.shortDescription} onChange={set("shortDescription")}
            placeholder="e.g. Maximum SUNSKY support with priority handling." />
        </div>
        <div className={styles.grid2}>
          <div className={styles.field}>
            <label>Internal Description</label>
            <textarea rows={2} value={form.internalDescription} onChange={set("internalDescription")}
              placeholder="Backend-only note…" />
          </div>
          <div className={styles.field}>
            <label>Sort Order</label>
            <input type="number" min={0} value={form.sortOrder} onChange={set("sortOrder")} />
            <span className={styles.hint}>Card order in checkout, lowest first.</span>
          </div>
        </div>

        {/* ── 2. Product & Pricing (§3.1, §6.2.2) ── */}
        <div className={styles.sectionTitle}>Product &amp; Pricing</div>
        {errors.products && <div className={styles.blockErr}>{errors.products}</div>}
        <div className={styles.productGrid}>
          {PRODUCTS.map(({ key, label, Icon }) => {
            const p = form.products.find((x) => x.productType === key);
            return (
              <div key={key} className={`${styles.productCard} ${p.enabled ? styles.productOn : ""}`}>
                <div className={styles.productHead}>
                  <span className={styles.productName}>
                    <Icon size={16} /> {label}
                  </span>
                  <ToggleSwitch checked={p.enabled} onChange={() => setProduct(key, { enabled: !p.enabled })} />
                </div>
                {p.enabled ? (
                  <>
                    <div className={styles.field}>
                      <label>Price (EUR)</label>
                      <input type="number" min={0} step="0.01" value={p.price}
                        onChange={(e) => setProduct(key, { price: e.target.value })} placeholder="0.00" />
                    </div>
                    <div className={styles.field}>
                      <label>Pricing Unit</label>
                      <select value={p.pricingUnit} onChange={(e) => setProduct(key, { pricingUnit: e.target.value })}>
                        {PRICING_UNITS.map((u) => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </select>
                    </div>
                    <label className={styles.checkRow}>
                      <input type="checkbox" checked={p.isDefault}
                        onChange={(e) => setProduct(key, { isDefault: e.target.checked })} />
                      <span>Default for this product</span>
                    </label>
                    <span className={styles.hint}>
                      Setting this as default removes the default from whichever level has it now.
                    </span>
                  </>
                ) : (
                  <p className={styles.productOff}>Not sold for this product.</p>
                )}
              </div>
            );
          })}
        </div>

        {/* ── 4. Services (§5, §6.2.4) ── */}
        <div className={styles.sectionTitle}>
          Services
          <button type="button" className={styles.addService} onClick={addService}
            disabled={catalogue.length === 0 || form.services.length >= catalogue.length}>
            <Plus size={14} /> Add Service
          </button>
        </div>
        {errors.services && <div className={styles.blockErr}>{errors.services}</div>}
        {catalogue.length === 0 && (
          <p className={styles.hint}>No active support services yet. Create them under Support Services first.</p>
        )}
        {form.services.length === 0 ? (
          <p className={styles.hint}>No services assigned to this level yet.</p>
        ) : (
          <div className={styles.serviceList}>
            {form.services.map((s) => (
              <div key={s._key} className={`${styles.serviceRow} ${dupes.has(s._key) ? styles.rowInvalid : ""}`}>
                <select className={styles.serviceSelect} value={s.supportServiceId}
                  onChange={(e) => setService(s._key, { supportServiceId: Number(e.target.value) })}>
                  {catalogue.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                  {/* An assignment can outlive its service being deactivated (§7), and that row
                      must still render rather than silently snapping to another service. */}
                  {!catalogue.some((c) => c.id === Number(s.supportServiceId)) && (
                    <option value={s.supportServiceId}>{nameOf(s.supportServiceId)} (inactive)</option>
                  )}
                </select>

                <select value={s.productType} onChange={(e) => setService(s._key, { productType: e.target.value })}>
                  {ASSIGNMENT_PRODUCTS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>

                <select value={s.status} onChange={(e) => setService(s._key, { status: e.target.value })}>
                  {STATUSES.map((st) => (
                    <option key={st.value} value={st.value}>{st.label}</option>
                  ))}
                </select>

                {s.status === "PAID" ? (
                  <>
                    <input className={styles.feeInput} type="number" min="0.01" step="0.01" value={s.serviceFee}
                      onChange={(e) => setService(s._key, { serviceFee: e.target.value })} placeholder="Fee" />
                    <select value={s.feeUnit} onChange={(e) => setService(s._key, { feeUnit: e.target.value })}>
                      {FEE_UNITS.map((u) => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <span className={styles.feeSpacer} />
                )}

                <button type="button" className={styles.rowDelete} title="Remove"
                  onClick={() => setForm((f) => ({ ...f, services: f.services.filter((x) => x._key !== s._key) }))}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── 3. Status (§6.2.3) ── */}
        <div className={styles.sectionTitle}>Status</div>
        <div className={styles.toggleRow}>
          <div>
            <label>Active</label>
            <span className={styles.hint}>
              Inactive levels are not offered for new bookings. Bookings already made keep the
              package they were sold.
            </span>
          </div>
          <ToggleSwitch checked={form.active} onChange={() => setForm((f) => ({ ...f, active: !f.active }))} />
        </div>
      </form>
    </BaseModal>
  );
};

export default SupportLevelModal;
